import { isNil, chain, isArray, isObject, isEmpty, isString, isDate } from 'lodash';

export class RsUtils {
  /** Allow specific keys and key combinations */
  public static isAllowableKeyOrKeyCombination(event: KeyboardEvent): boolean {
    const
      allowedUncombinedKeys = [
        'Enter',
        'Backspace',
        'Tab',
        'Escape',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End'
      ],
      keysAllowedToBeCombinedWithCtrl = [
        'a',
        'x',
        'c',
        'v'
      ];

    return allowedUncombinedKeys.includes(event.key)
            || ((event.ctrlKey || event.metaKey) && keysAllowedToBeCombinedWithCtrl.includes(event.key));
  }

  /** Fake user typing into the input field + triggers keyDown, keyUp, keyPress on the way
     *
     * If keydown event is prevented by some keydown rules, it won't type the char
     *
     * Used to test form field directives
     * */
  public static fakeUserTyping(
    value: string,
    inputElement: HTMLInputElement
  ): void {
    for (const char of value) {
      const
        keyDownMock = new KeyboardEvent('keydown', { key: char, cancelable: true, bubbles: true }),
        keyUpMock = new KeyboardEvent('keyup', { cancelable: true, bubbles: true }),
        keyPressMock = new KeyboardEvent('keypress', { cancelable: true, bubbles: true });

      inputElement.dispatchEvent(keyDownMock);

      if (
        !keyDownMock.defaultPrevented
                && !keyUpMock.defaultPrevented
                && !keyPressMock.defaultPrevented
      ) {
        inputElement.value = inputElement.value.concat(char);
        inputElement.dispatchEvent(new Event('input'));
      }

      inputElement.dispatchEvent(keyUpMock);
      inputElement.dispatchEvent(keyPressMock);
    }
  }

  public static trimEmptyHttpParams(params: Object): {} {
    return chain(params)
      .entries()
      .filter(([, value]) => {
        if (isDate(value)) {
          return !isNil(value);
        }
        if (isString(value) || isArray(value) || isObject(value)) {
          return !isEmpty(value);
        }
        return !isNil(value);
      })
      .fromPairs()
      .value();
  }

  public static isElementInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    return (
      rect.bottom >= 0 &&
      rect.right >= 0 &&
      rect.top <=
      (window.innerHeight || document.documentElement.clientHeight) &&
      rect.left <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  public static isUUID = (uuid: string): boolean => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
  }

  public static upperFirst(str: string = ''): string {
    if (!str) { return ''; }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
